import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, ClipboardList, Pencil, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@/components/ui/Card";
import { BattererSessionForm } from "./BattererSessionForm";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BattererSessionRecord {
  id: number;
  person_id: number;
  agency_id: number;
  session_date: string;
  session_time: string | null;
  session_status: string;
  dynamics: string | null;
  group_behavior: string | null;
  individual_behavior: string | null;
  program_name: string | null;
  service_site_id: number | null;
  created_at: string;
}

interface SessionCounts {
  total: number;
  present: number;
  absent: number;
  excused: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface BattererSessionsProps {
  personId: string;
}

const statusColors: Record<string, string> = {
  present: "bg-green-100 text-green-800",
  absent: "bg-red-100 text-red-800",
  excused: "bg-amber-100 text-amber-800",
  late: "bg-orange-100 text-orange-800",
};

export function BattererSessions({ personId }: BattererSessionsProps) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingSession, setEditingSession] = useState<BattererSessionRecord | null>(null);

  const { data, isLoading } = useQuery<{ data: BattererSessionRecord[] }>({
    queryKey: ["battererSessions", personId],
    queryFn: () =>
      api.get<{ data: BattererSessionRecord[] }>(
        `/clients/${personId}/batterer-sessions`,
      ),
    enabled: !!personId,
  });

  const { data: countsData } = useQuery<{ data: SessionCounts }>({
    queryKey: ["battererSessionCounts", personId],
    queryFn: () =>
      api.get<{ data: SessionCounts }>(
        `/clients/${personId}/batterer-sessions/counts`,
      ),
    enabled: !!personId,
  });

  const createMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post(`/clients/${personId}/batterer-sessions`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["battererSessions", personId] });
      queryClient.invalidateQueries({ queryKey: ["battererSessionCounts", personId] });
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...body }: { id: number } & Record<string, unknown>) =>
      api.put(`/clients/${personId}/batterer-sessions/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["battererSessions", personId] });
      queryClient.invalidateQueries({ queryKey: ["battererSessionCounts", personId] });
      setEditingSession(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      api.delete(`/clients/${personId}/batterer-sessions/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["battererSessions", personId] });
      queryClient.invalidateQueries({ queryKey: ["battererSessionCounts", personId] });
    },
  });

  const sessions = data?.data ?? [];
  const counts = countsData?.data;

  return (
    <div className="space-y-4">
      {/* Summary counts */}
      {counts && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{counts.total}</p>
              <p className="text-xs text-muted-foreground">Total Sessions</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{counts.present}</p>
              <p className="text-xs text-muted-foreground">Present</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-red-600">{counts.absent}</p>
              <p className="text-xs text-muted-foreground">Absent</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-600">{counts.excused}</p>
              <p className="text-xs text-muted-foreground">Excused</p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">Batterer Intervention Sessions</h3>
        <Button size="sm" className="gap-2" onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4" />
          Add Session
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardContent className="p-4">
            <BattererSessionForm
              onSubmit={(d) => createMutation.mutate(d)}
              onCancel={() => setShowForm(false)}
              isLoading={createMutation.isPending}
            />
          </CardContent>
        </Card>
      )}

      {editingSession && (
        <Card>
          <CardContent className="p-4">
            <BattererSessionForm
              initial={editingSession}
              onSubmit={(d) =>
                updateMutation.mutate({ id: editingSession.id, ...d })
              }
              onCancel={() => setEditingSession(null)}
              isLoading={updateMutation.isPending}
            />
          </CardContent>
        </Card>
      )}

      {isLoading && (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="h-20 animate-pulse rounded-lg border bg-muted"
            />
          ))}
        </div>
      )}

      {!isLoading && sessions.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <ClipboardList className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No batterer intervention sessions found.
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading &&
        sessions.map((s) => (
          <Card key={s.id}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-sm font-medium">
                    Session - {new Date(s.session_date).toLocaleDateString()}
                    {s.session_time && ` at ${s.session_time}`}
                  </CardTitle>
                  <CardDescription className="mt-0.5 flex items-center gap-2">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${
                        statusColors[s.session_status] ?? "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {s.session_status}
                    </span>
                    {s.program_name && (
                      <span className="text-xs text-muted-foreground">
                        {s.program_name}
                      </span>
                    )}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setEditingSession(s)}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      if (confirm("Delete this session?")) {
                        deleteMutation.mutate(s.id);
                      }
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {s.dynamics && (
                <p className="text-xs text-muted-foreground">
                  <span className="font-medium">Dynamics:</span> {s.dynamics}
                </p>
              )}
              {s.group_behavior && (
                <p className="text-xs text-muted-foreground mt-1">
                  <span className="font-medium">Group Behavior:</span> {s.group_behavior}
                </p>
              )}
              {s.individual_behavior && (
                <p className="text-xs text-muted-foreground mt-1">
                  <span className="font-medium">Individual Behavior:</span> {s.individual_behavior}
                </p>
              )}
            </CardContent>
          </Card>
        ))}
    </div>
  );
}
